import React, { useEffect, useState } from "react";
import Modal from "../Modals/Modal";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import InputSearch from "../inputs/InputSearch";
import { useLazyListCoursesQuery, useLazyListTopicsQuery } from "../../services/organization/adminCourse";
import DiscoverCourseCard from "../cards/DiscoverCourseCard";
import SelectFilter from "../inputs/SelectFilter";

const MODAL_ID = "add-course-to-track-modal";

export default function AddCourseToTrackModal({
  isOpened,
  onClose,
  onAddCourse,
  existingCourseIds = new Set(),
}) {
  const { t } = useTranslation(["learningPaths", "common"]);
  const { t: commonT } = useTranslation("common");

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [courses, setCourses] = useState([]);
  const [selectedCoursesMap, setSelectedCoursesMap] = useState(new Map());
  const [playingVideoId, setPlayingVideoId] = useState(null);

  const [selectedTopic, setSelectedTopic] = useState(null);

  const [triggerListCourses, { data: coursesData, isLoading }] =
    useLazyListCoursesQuery();
  
  const [fetchTopics, { data: topicsData }] = useLazyListTopicsQuery();

  useEffect(() => {
    fetchTopics();
  }, []);

  const allTopicOption = React.useMemo(
    () => ({ value: "", label: t("common:all") }),
    [t]
  );

  const tagesOptions = React.useMemo(
    () => [
      allTopicOption,
      ...(topicsData?.data?.map(({ slug, title }) => ({
        value: slug,
        label: title,
      })) ?? []),
    ],
    [topicsData, allTopicOption]
  );

  const selectedTopicOption = React.useMemo(
    () =>
      tagesOptions.find((option) => option.value === (selectedTopic ?? "")) ??
      allTopicOption,
    [tagesOptions, selectedTopic, allTopicOption]
  );

  useEffect(() => {
    if (isOpened) {
      setPage(1);
      setSearch("");
      setCourses([]);
      setCourses([]);
      setSelectedCoursesMap(new Map());
      setPlayingVideoId(null);
      setSelectedTopic(null);
    }
  }, [isOpened]);

  useEffect(() => {
    if (isOpened) {
      const queryParams = [];
      if (search) queryParams.push(`filters[keyword]=${search}`);
      if (selectedTopic) queryParams.push(`filters[topic]=${selectedTopic}`);
      
      if (existingCourseIds && existingCourseIds.size > 0) {
        queryParams.push(
          `filters[exclude_ids]=${Array.from(existingCourseIds).join(",")}`,
        );
      }
      const filters = queryParams.join("&");
      triggerListCourses({ page, filters });
    }
  }, [page, search, selectedTopic, isOpened, triggerListCourses, existingCourseIds]);

  useEffect(() => {
    if (isOpened && coursesData?.data) {
      if (page === 1) {
        setCourses(coursesData.data);
      } else {
        setCourses((prev) => {
          const existingIds = new Set(prev.map((c) => c.id));
          const newCourses = coursesData.data.filter(
            (c) => !existingIds.has(c.id),
          );
          return [...prev, ...newCourses];
        });
      }
    }
  }, [coursesData, page, isOpened]);

  useEffect(() => {
    const el = document.getElementById(MODAL_ID);
    if (!el) return;
    const bootstrapModal = window.bootstrap?.Modal?.getOrCreateInstance(el);
    if (window.bootstrap) {
      if (isOpened) {
        bootstrapModal.show();
      } else {
        bootstrapModal.hide();
      }
      return () => bootstrapModal.hide();
    }
  }, [isOpened]);

  useEffect(() => {
    const el = document.getElementById(MODAL_ID);
    if (!el) return;
    const handleHidden = () => onClose?.();
    el.addEventListener("hidden.bs.modal", handleHidden);
    return () => el.removeEventListener("hidden.bs.modal", handleHidden);
  }, [onClose]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
    setCourses([]);
  };
  const handleTopicChange = (data) => {
    setSelectedTopic(data.value);
    setPage(1);
    setCourses([]);
  };
  const handleToggleCourse = (course) => {
    if (existingCourseIds.has(course.id)) return;

    setSelectedCoursesMap((prev) => {
      const next = new Map(prev);
      if (next.has(course.id)) {
        next.delete(course.id);
      } else {
        next.set(course.id, course);
      }
      return next;
    });
  };

  const handleAddCourses = () => {
    const selectedCourses = Array.from(selectedCoursesMap.values());
    onAddCourse(selectedCourses);
    setSelectedCoursesMap(new Map());
  };

  return (
    <Modal
      id={MODAL_ID}
      title={t("learningPaths:addCourseToTrack")}
      isOpened={isOpened}
      onClose={onClose}
      maxWidth={1200}
      className="add-course-to-track-modal modal-full-height"
    >
      <div className="add-course-track-modal">
        <div className="mb-3 row row-cols-1 gy-2 add-course-track-modal__filters">
          <div className="col-md-8">
            <InputSearch
              placeHolder={t("learningPaths:courseName")}
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <div className="col-md-4">
            <SelectFilter
                label={""}
                options={tagesOptions}
                value={selectedTopicOption}
                onchange={handleTopicChange}
                className="mb-0 add-course-select"
              />
          </div>
        </div>

        <div id="scrollableCoursesDiv" className="scrollable-infinite-modal">
          {isLoading && page === 1 && courses.length === 0 ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{commonT("Loading")}</span>
              </div>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={courses.length}
              next={() => setPage((p) => p + 1)}
              hasMore={!!coursesData?.pagination?.next}
              loader={
                <h4 className="text-center mt-3 span-full">
                  {commonT("Loading")}
                </h4>
              }
              scrollableTarget="scrollableCoursesDiv"
              className="row"
            >
              {courses.map((course) => (
                <DiscoverCourseCard
                  key={course.id}
                  imageUrl={course.image_url}
                  cousreName={course.title}
                  courseTitle={course.title}
                  courseTag={course.topics?.[0]}
                  color={course.topics?.[0]?.color}
                  avatarUrl={course.tutor?.avatar_url}
                  userName={course.tutor?.name}
                  description={course.description}
                  keyPoints={course.key_points}
                  details={course.details}
                  addLearningTrack={false}
                  promoVideoUrl={course.promo_video_url}
                  isPlaying={playingVideoId === course.id}
                  onPlay={() => setPlayingVideoId(course.id)}
                  onStop={() => setPlayingVideoId(null)}
                  courseDuration={course.video_duration}
                  courseLessons={course.lessons_count}
                  selectable={existingCourseIds.has(course.id) ? false : true}
                  isSelected={selectedCoursesMap.has(course.id)}
                  className={
                    existingCourseIds.has(course.id)
                      ? "opacity-50 pointer-events-none"
                      : ""
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setPlayingVideoId(null);
                    handleToggleCourse(course);
                  }}
                />
              ))}
            </InfiniteScroll>
          )}
        </div>

        {selectedCoursesMap.size > 0 && (
          <div className="sticky-footer-action d-flex gap-2">
            <button
              className="btn btn-grey-color w-100 py-2 fw-bold"
              onClick={() => setSelectedCoursesMap(new Map())}
            >
              {t("learningPaths:resetCourseSelection")}
            </button>
            <button
              className="btn btn-main-color font-15 w-100"
              onClick={handleAddCourses}
            >
              {t("learningPaths:addSelectedCoursesCount", {
                count: selectedCoursesMap.size,
              })}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
